/* eslint-disable react-hooks/exhaustive-deps */
import {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TokenexIFrameControlProps } from '../../../@types';
import {
  flattenStyles,
  parseStyles,
  useHostThemeForTokenex,
  useTokenexConfig,
} from '../../../hooks';
import { asyncInterval } from '../../../utils/interval';
import { CardBrandIcon } from '../CardBrandIcon';
import { createLogLocal } from '../../../utils/createLogLocal';
import { useCreditCardFormStore } from '../../../hooks/useCreditCardFormStore';
import { getCardBrandDisplayName } from '../../../utils/getCardBrandDisplayName';
import { getCardBrandTokenexName } from '../../../utils/getCardBrandTokenexName';

const log = createLogLocal('TokenexIFrameControl.tsx');

const TokenExSessionExpiredMessage =
  'Invalid Authentication Key - Session expired';
const TokenExOtherErrorMessage = 'An error occurred. Please contact support.';

const extractStylesGroup = (
  styles: Record<string, any>,
  starts: string,
): [Record<string, string>, string[]] => {
  const styleNames = Object.keys(styles).filter(s => s.startsWith(starts));
  const extracted = Object.fromEntries(styleNames.map(s => [s, styles[s]]));
  return [extracted, styleNames];
};

const getBrandErrorMessage = (
  cardBrands: string[],
  t: ReturnType<typeof useTranslation>['t'],
): string => {
  const displayBrands = cardBrands.map(card => getCardBrandDisplayName(card));
  return `${t('unsupportedCardType')} ${t('creditCardTypesAccepted')}: ${displayBrands.join(', ')}`;
};

/**
 * Validates the card brand against the list of accepted brands.
 *
 * Avoids checking if the field is not a card number (other fields either
 * don't have a brand, or default to outputting 'unknown').
 *
 * If setError is provided, updates the UI with an error if the validation fails.
 *
 * @param cardBrand Currently active brand from the Tokenex iFrame.
 * @param cardBrands List of card brands the property accepts.
 * @param fieldName Only validate if dealing with a card number field.
 * @param t Translation function.
 * @param [setError] Optional function to set an error on the field. If not
 *                   provided, validateBrand won't display the error.
 * @returns {false | {type: string; message: string; key: string}} false if
 *          the brand is valid, an error object if it's invalid.
 */
const validateBrand = (
  cardBrand: string,
  cardBrands: string[],
  fieldName: string,
  t: ReturnType<typeof useTranslation>['t'],
  setError?: (fieldName: string, errorData?: any) => void,
) => {
  // Ignore for fields that won't have a card brand (all but cardNumber).
  if (fieldName !== 'cardNumber') return false;

  // Ignore if card type is unknown
  if (cardBrand === 'unknown') {
    if (setError) setError(fieldName, undefined);
    return false;
  }

  // Handle special card cases: ensure all variants are included
  const cardBrandsClean = cardBrands.map(name => getCardBrandTokenexName(name));
  const cardBrandClean = getCardBrandTokenexName(cardBrand);

  // Check if the card brand is supported
  if (!cardBrandsClean.includes(cardBrandClean)) {
    const errorData = {
      type: 'validate',
      message: getBrandErrorMessage(cardBrandsClean, t),
      key: fieldName,
    };
    if (setError) setError(fieldName, errorData);
    return errorData;
  }
  return false;
};

/**
 * Field where the actual card number and CVV are both rendered.
 */
export const TokenexIFrameControl = forwardRef(
  (props: TokenexIFrameControlProps, ref) => {
    const {
      scheme,
      id,
      onLoadStart,
      onLoadEnd,
      onFocusHandler,
      onBlurHandler,
      onChangeHandler,
      onErrorHandler,
      name: fieldName,
      label,
      helper,
      isDisabled = false,
      // isRequired = false,
      validateOnBlur = true,
      validateOnChange = false,
    } = props;

    const [isPCI, setPCIFlag] = useState<boolean>(false);
    const cardBrands = useCreditCardFormStore(store => store.cardBrands);
    const cardBrand = useCreditCardFormStore(store => store.cardBrand);
    const getCardBrand = useCreditCardFormStore(store => store.getCardBrand);
    const setCardBrand = useCreditCardFormStore(store => store.setCardBrand);
    log('props', props, 'cardBrand:', cardBrand, 'cardBrands:', cardBrands);

    const { t } = useTranslation();
    const tokenexTheme = useHostThemeForTokenex();
    const { getTokenexConfig } = useTokenexConfig(scheme);
    const {
      register,
      setError,
      setValue,
      clearErrors,
      // trigger,
      formState: { errors },
    } = useFormContext();

    const iframeRef = useRef<any>();
    const tokenizedRef = useRef<boolean>();
    const validationRef = useRef<boolean>();

    register(fieldName, {
      // validate: {
      //   nativeValidation: () => {
      //     window.console.log('ddddddddd');
      //     iframeRef.current.validate();
      //     return true;
      //   },
      // },
    });

    const isInvalid = Boolean(errors[fieldName]?.message);

    useEffect(() => {
      if (tokenexTheme === null) return;
      if (onLoadStart) onLoadStart();

      if (!iframeRef.current) {
        // TODO: this needs to be moved out somewhere, idk where now
        // used to eleminate scroll on tokenex iframe
        const tokenexStyles = parseStyles(tokenexTheme.css.base);
        const tokenexHeight = parseInt(tokenexStyles.height, 10);
        const containerHeight =
          document.getElementById(id)?.clientHeight || 10 * 1000; // just some random big number

        if (!tokenexHeight || tokenexHeight > containerHeight) {
          tokenexStyles.height = `${containerHeight}px`;
          tokenexTheme.css.base = flattenStyles(tokenexStyles);
        }

        const tokenexConfig = getTokenexConfig(tokenexTheme.css);

        setPCIFlag(tokenexConfig?.pci || false);
        iframeRef.current = new (window as any).TokenEx.Iframe(
          id,
          tokenexConfig,
        );

        iframeRef.current.on('load', () => {
          if (onLoadEnd) onLoadEnd();
        });
        iframeRef.current.on('focus', () => {
          if (onFocusHandler) onFocusHandler();
        });
        iframeRef.current.on('blur', () => {
          log(
            `[@cloudbeds/payment-tokenization] ${scheme} onBlur`,
            validateOnBlur,
          );
          if (validateOnBlur) iframeRef.current.validate();
          if (onBlurHandler) onBlurHandler();
        });
        iframeRef.current.on('change', (data: any) => {
          log(
            `[@cloudbeds/payment-tokenization] ${scheme} onChange`,
            validateOnChange,
            data,
          );
          if (validateOnChange) iframeRef.current.validate();
          if (onChangeHandler) onChangeHandler(fieldName as any);
        });

        iframeRef.current.on('validate', (data: any) => {
          validationRef.current = undefined;
          log('iframe->validate', 'data', data, 'props', props);

          // Get data to detect unsupported card brands error
          const card =
            getCardBrand() ||
            data.cardType ||
            data.possibleCardType ||
            cardBrand;
          const brandError =
            card && validateBrand(card, cardBrands, fieldName, t);

          // Handle validation errors
          if (brandError || data.isValid === false) {
            validationRef.current = false;
            const errType = data?.validator;

            const errorData = brandError || {
              type: errType || 'validate',
              message:
                errType === 'required' ? t('requiredField') : t('invalidField'),
              key: fieldName,
            };

            if (onErrorHandler && (validateOnBlur || validateOnChange))
              onErrorHandler({ [fieldName]: errorData });

            setError(fieldName, errorData);
            setValue(fieldName, undefined);
          }

          // Remove error messages if validation passes
          else {
            validationRef.current = true;
            clearErrors(fieldName);
          }
        });

        iframeRef.current.on('cardTypeChange', (data: any) => {
          // Don't set the card type based on CVV field output (it results
          // in the card type getting repeatedly set to 'unknown')
          if (fieldName !== 'cardNumber') return;

          log('iframe->cardTypeChange: data', data, 'cardBrands', cardBrands);

          const cardType = data?.possibleCardType || 'unknown';
          const card = getCardBrandTokenexName(cardType);
          setCardBrand(card);

          if (validateBrand(card, cardBrands, fieldName, t, setError)) return;

          // Clear errors if card type is valid
          setValue(fieldName, undefined);
          clearErrors(fieldName);
        });

        iframeRef.current.on('tokenize', (data: any) => {
          // window.console.log(`${scheme} onTokenize`);
          log('[@cloudbeds/payment-tokenization]  token:', data);
          tokenizedRef.current = true;
          setValue(fieldName, data);
          // if (onTokenize) onTokenize();
        });
        iframeRef.current.on('error', (data: any) => {
          log('iframe->error', 'data:', data);
          let error = {
            type: 'other',
            message: TokenExOtherErrorMessage,
            key: fieldName,
          };

          if (data.error === TokenExSessionExpiredMessage) {
            data.validator = 'session';
            data.isValid = false;

            error = {
              type: 'session',
              message: TokenExSessionExpiredMessage,
              key: fieldName,
            };
          } else if (data.error) {
            data.validator = 'other';
            data.isValid = false;

            error = {
              type: 'other',
              message: data.error || TokenExOtherErrorMessage,
              key: fieldName,
            };
          }

          setError(fieldName, error);
          setValue(fieldName, undefined);

          window.console.error(`[tokenex -> ${scheme}]:`, data);
          if (onErrorHandler) onErrorHandler({ [fieldName]: error });
        });

        iframeRef.current.load();
      }
    }, []);

    useImperativeHandle(ref, () => ({
      async beforeSubmit() {
        tokenizedRef.current = undefined;

        iframeRef.current.tokenize();

        try {
          await asyncInterval(() => tokenizedRef.current);
          return true;
        } catch (_e) {
          return false;
        }
      },
      async validate() {
        validationRef.current = undefined;

        iframeRef.current.validate();
        // await trigger();

        try {
          await asyncInterval(() => validationRef.current);
          return true;
        } catch (_e) {
          return false;
        }
      },
    }));

    const [borderStyles] = extractStylesGroup(
      tokenexTheme?.module?.base,
      'border',
    );

    const wrapperStyles = {
      paddingLeft: isPCI ? '0' : '0', // well... ¯\_(ツ)_/¯
      height: tokenexTheme?.module?.base?.height || 'auto',
      ...borderStyles,
    };

    return (
      <Box>
        <FormControl
          isInvalid={isInvalid}
          isDisabled={isDisabled}
          isRequired={true}
          className={cx('IFrameContainer')}
        >
          <FormLabel className={cx('InputLabel')}>{label}</FormLabel>

          <div
            style={{
              display: 'flex',
            }}
            className={cx('Input')}
          >
            {isPCI && (
              <Box
                style={{ alignSelf: 'center', paddingRight: '6px' }}
                {...(fieldName === 'cardNumber' && cardBrand !== 'unknown'
                  ? { title: getCardBrandDisplayName(cardBrand) }
                  : {})}
              >
                <CardBrandIcon brand={getCardBrandTokenexName(cardBrand)} />
              </Box>
            )}

            <div
              id={id}
              style={wrapperStyles}
              {...(fieldName === 'cardNumber' && cardBrand !== 'unknown'
                ? { title: getCardBrandDisplayName(cardBrand) }
                : {})}
            />
          </div>

          {helper && !isInvalid && (
            <FormHelperText className={cx('InputHelper')}>
              {helper}
            </FormHelperText>
          )}
          {isInvalid && (
            <FormErrorMessage className={cx('InputError')}>
              {errors[fieldName]?.message as string}
            </FormErrorMessage>
          )}
        </FormControl>
      </Box>
    );
  },
);
